import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Archive, Code2, Eye, Loader2, Maximize2, Minimize2, Save, Send, Settings2, ShieldCheck, Tag, Trash2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KbRichEditor } from "@/components/KbRichEditor";
import { KbSectionsEditor } from "@/components/KbSectionsEditor";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";
import { KbVisibilityPicker } from "@/components/KbVisibilityPicker";
import { KbRevisionsPanel } from "@/components/KbRevisionsPanel";
import { KbCommentsPanel } from "@/components/KbCommentsPanel";
import { KbReviewPanel } from "@/components/KbReviewPanel";
import { KbBacklinkWarning } from "@/components/KbBacklinkWarning";
import { KbBrokenImageWarning } from "@/components/KbBrokenImageWarning";
import { KbBrokenEmbedWarning } from "@/components/KbBrokenEmbedWarning";
import { KbRelatedPicker } from "@/components/KbRelatedPicker";
import { KbImageFixBar } from "@/components/KbImageFixBar";
import { KbEmbedFixBar } from "@/components/KbEmbedFixBar";
import { KbAiAssistPanel } from "@/components/KbAiAssistPanel";
import { KbEditingPresence } from "@/components/KbEditingPresence";
import { KbStyleInjector } from "@/lib/kbSettings";
import { sanitizeKbHtml, useKbEmbedHosts } from "@/lib/kbSanitize";
import { enhanceKbContent } from "@/lib/kbContentRuntime";
import { KbSnippetInfo } from "@/components/KbSnippetInfo";
import { KbReviewerSelect } from "@/components/KbReviewerSelect";
import { useAuth } from "@/context/AuthContext";

const emptyForm = {
  name: "", description: "", meta_description: "", slug: "", body_html: "",
  audiences: ["internal"], target_teams: [], target_orgs: [], keywords: [], tags: [],
  related_ids: [],
  sections: [], primary_category_id: "all", publish_at: "", glossary_off: false,
};

// Schedules are entered and displayed in the company's timezone.
const TZ = "America/Toronto";
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};
const fromLocalInput = (value) => {
  if (!value) return "";
  // Work out Toronto's offset for that moment, then build a real UTC instant.
  const guess = new Date(`${value}:00Z`);
  const label = new Intl.DateTimeFormat("en-US", { timeZone: TZ, timeZoneName: "shortOffset" })
    .format(guess).split("GMT")[1] || "-5";
  const [h, m = "0"] = label.split(":");
  const offsetMinutes = parseInt(h, 10) * 60 + (parseInt(h, 10) < 0 ? -parseInt(m, 10) : parseInt(m, 10));
  return new Date(guess.getTime() - offsetMinutes * 60000).toISOString();
};
const scheduleLabel = (iso) => (iso
  ? new Date(iso).toLocaleString("en-CA", {
    timeZone: TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }) + " ET"
  : "");


// Where the article stands for the command bar. A published article with an
// edit waiting for review is still public — the pill says so.
const deriveStatus = (a) => {
  if (!a) return "draft";
  if (!a.is_draft) return a.pending ? "published_pending" : "published";
  return a.review_state === "in_review" ? "in_review" : "draft";
};
const PILL = {
  draft: ["Draft", "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"],
  in_review: ["In review", "bg-amber-100 text-amber-700"],
  published: ["Published", "bg-emerald-100 text-emerald-700"],
  published_pending: ["Published · update in review", "bg-emerald-100 text-emerald-700"],
};

// Health rows store the raw HTML src, so a query string keeps its `&amp;` —
// the DOM hands it back decoded. Compare both ways.
const sameUrl = (a, b) => (a || "").replace(/&amp;/g, "&") === (b || "").replace(/&amp;/g, "&");

const locateFix = (target, kind) => {
  if (kind === "image") {
    return [...document.querySelectorAll(".ProseMirror img")]
      .find((i) => sameUrl(i.getAttribute("src"), target));
  }
  const frame = [...document.querySelectorAll(".ProseMirror iframe")]
    .find((f) => sameUrl(f.getAttribute("src"), target));
  return frame ? (frame.closest(".kb-embed") || frame) : undefined;
};

/** Find the thing an editor arrived to replace, ring it, and report where it is
 *  so the fix panel can float against it instead of sitting up at the top of
 *  the page away from the content it is about. */
function useFixAnchor(target, ready, kind, onMissing) {
  const [anchor, setAnchor] = useState(null);
  useEffect(() => {
    if (!target || !ready) { setAnchor(null); return undefined; }
    let tries = 0;
    let found = null;
    const place = () => {
      if (!found?.isConnected) return;
      const r = found.getBoundingClientRect();
      setAnchor({ top: r.bottom, left: r.left });
    };
    const iv = setInterval(() => {
      tries += 1;
      const el = locateFix(target, kind);
      if (el && el !== found) {
        found?.classList.remove("kb-fix-target");
        found = el;
        el.classList.add("kb-fix-target");
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        place();
      } else if (el) place();
      else if (tries >= 12) { clearInterval(iv); setAnchor(null); onMissing?.(); }
    }, 400);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      clearInterval(iv);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      found?.classList.remove("kb-fix-target");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, ready, kind]);
  return anchor;
}

/** Swap one URL for another everywhere it appears in the body HTML. */
const swapUrl = (html, from, to) => {
  const plain = (from || "").replace(/&amp;/g, "&");
  const variants = [...new Set([from, plain, plain.replace(/&/g, "&amp;")])];
  let out = html || "";
  variants.forEach((v) => { if (v) out = out.split(v).join(to); });
  return out;
};


export default function KBArticleEditor() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const embedHosts = useKbEmbedHosts();
  const isAdmin = user?.role === "super_admin";
  const isCreate = !slug;
  const isSnippet = params.get("snippet") === "1";

  const [article, setArticle] = useState(null);
  const [tree, setTree] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("draft");
  const [reviewer, setReviewer] = useState("");
  const [mode, setMode] = useState("visual");        // visual | html | preview
  const [pane, setPane] = useState("settings");      // settings | review | history | ai
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const previewRef = useRef(null);
  const [savedAt, setSavedAt] = useState(null);

  // Arrived from Image & link health with a specific picture or video to sort
  // out — find it, ring it and float the fix panel against it, rather than
  // leaving the editor to hunt through forty screenshots for the dead one.
  const fixUrl = params.get("fix");
  const fixEmbedUrl = params.get("fixembed");
  const [fixReplaced, setFixReplaced] = useState(false);
  const [fixRemaining, setFixRemaining] = useState(0);
  const [fixTarget, setFixTarget] = useState("");   // the src to ring right now
  const [fixQueue, setFixQueue] = useState([]);     // in-editor walk, no health list involved
  const [pastImages, setPastImages] = useState([]); // pictures this article used to show

  useEffect(() => {
    if (!fixTarget || isCreate || fixReplaced) { setPastImages([]); return; }
    api.get(`/kb/articles/${slug}/past-images`, { params: { url: fixTarget } })
      .then((r) => setPastImages(r.data.images || [])).catch(() => setPastImages([]));
  }, [fixTarget, fixReplaced, slug, isCreate]);
  const [embedReplaced, setEmbedReplaced] = useState(false);
  const [embedRemaining, setEmbedRemaining] = useState(0);
  const [embedTarget, setEmbedTarget] = useState("");
  useEffect(() => { setFixReplaced(false); setFixTarget(fixUrl || ""); setFixQueue([]); }, [fixUrl]);
  useEffect(() => { setEmbedReplaced(false); setEmbedTarget(fixEmbedUrl || ""); }, [fixEmbedUrl]);

  const fixAnchor = useFixAnchor(fixTarget, !!article, "image",
    () => toast.info("That picture isn't in the article any more — nothing to replace"));
  const embedAnchor = useFixAnchor(embedTarget, !!article, "embed",
    () => toast.info("That video isn't in the article any more — nothing to replace"));

  useEffect(() => {
    if (!fixUrl) { setFixRemaining(0); return; }
    api.get("/kb/link-health/next", { params: { kind: "image", slug, url: fixUrl } })
      .then((r) => setFixRemaining(r.data.remaining || 0)).catch(() => {});
  }, [fixUrl, slug]);
  useEffect(() => {
    if (!fixEmbedUrl) { setEmbedRemaining(0); return; }
    api.get("/kb/link-health/next", { params: { kind: "embed", slug, url: fixEmbedUrl } })
      .then((r) => setEmbedRemaining(r.data.remaining || 0)).catch(() => {});
  }, [fixEmbedUrl, slug]);
  const [kwDraft, setKwDraft] = useState("");
  const [tagDraft, setTagDraft] = useState("");
  const [activity, setActivity] = useState(0);
  const [loading, setLoading] = useState(!isCreate);
  const [focus, setFocus] = useState(false);   // hide chrome for a wide canvas
  const saveRef = useRef(null);
  const savedBodyRef = useRef("");
  const [backlinkAsk, setBacklinkAsk] = useState("");

  const archiveArticle = async () => {
    const going = !article?.archived;
    try {
      const { data } = await api.post(`/kb/articles/${slug}/archive`, { archived: going });
      setArticle(data);
      toast.success(going ? "Archived — hidden from readers, restorable from Manage articles" : "Restored");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/kb/tree").then((r) => setTree(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isCreate) return;
    setLoading(true);
    api.get(`/kb/articles/${slug}`, { params: { count_view: 0, for_edit: 1 } }).then((r) => {
      const live = r.data;
      setArticle(live);
      // A published article keeps its live copy for readers; the editor works
      // on the version under review (if there is one) layered on top.
      const a = { ...live, ...((live.pending || {}).fields || {}) };
      setForm({
        name: a.name || "", description: a.description || "",
        meta_description: a.meta_description || "", slug: a.slug || "",
        body_html: a.body_html || "", audiences: a.audiences || ["internal"],
        target_teams: a.target_teams || [], target_orgs: a.target_orgs || [],
        keywords: a.keywords || [], tags: a.tags || [], sections: a.sections || [],
        related_ids: a.related_ids || (live.related || []).map((r) => r.article_id),
        primary_category_id: a.primary_category_id || "all",
        publish_at: a.publish_at || "",
        glossary_off: a.glossary_off === true,
      });
      setStatus(deriveStatus(live));
      setReviewer(live.reviewer_id || "");
      savedBodyRef.current = a.body_html || "";
      setDirty(false);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [slug, isCreate]);

  const patch = (upd) => { setForm((f) => ({ ...f, ...upd })); setDirty(true); };

  const payload = useMemo(() => ({
    name: form.name, description: form.description, meta_description: form.meta_description,
    slug: form.slug, body_html: form.body_html, audiences: form.audiences,
    target_teams: form.target_teams, target_orgs: form.target_orgs,
    keywords: form.keywords, tags: form.tags, sections: form.sections,
    related_ids: form.related_ids || [],
    publish_at: form.publish_at || "",
    glossary_off: !!form.glossary_off,
    primary_category_id: form.primary_category_id !== "all" ? form.primary_category_id : null,
    category_ids: form.primary_category_id !== "all" ? [form.primary_category_id] : [],
    reviewer_id: reviewer || undefined,
  }), [form, reviewer]);

  const save = useCallback(async (nextStatus, { silent = false } = {}) => {
    if (!reviewer && !isAdmin) { if (!silent) toast.error("Pick a reviewer — every article and snippet needs one"); return null; }
    if (!form.name.trim()) { if (!silent) toast.error("A title is required"); return null; }
    setSaving(true);
    try {
      if (isCreate) {
        const { data } = await api.post("/kb/articles", {
          ...payload, is_snippet: isSnippet,
          status: nextStatus === "in_review" ? "in_review" : nextStatus === "published" ? "published" : "draft",
        });
        toast.success(nextStatus === "in_review" ? "Submitted — the reviewer has been notified"
          : nextStatus === "published" ? "Published" : "Draft created");
        navigate(`/kb/a/${data.slug}/edit`, { replace: true });
        return data;
      }
      const body = { ...payload };
      if (nextStatus === "draft" && status.startsWith("published")) body.status = "draft";   // unpublish
      if (nextStatus === "published") body.status = "published";                             // super admin only
      const { data } = await api.patch(`/kb/articles/${slug}`, body);
      setArticle(data);
      setStatus(deriveStatus(data));
      setDirty(false);
      setSavedAt(new Date());
      savedBodyRef.current = payload.body_html || "";
      if (data.slug && data.slug !== slug && !data.pending) navigate(`/kb/a/${data.slug}/edit`, { replace: true });
      if (!silent) {
        toast.success(nextStatus === "published" ? "Published — this version is live now"
          : nextStatus === "draft" && status.startsWith("published") ? "Unpublished — hidden from readers"
          : data.pending ? "Saved — readers keep the published version until your reviewer approves"
            : "Saved");
      }
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return null;
    } finally { setSaving(false); }
  }, [form.name, isAdmin, isCreate, isSnippet, navigate, payload, reviewer, slug, status]);

  // "Submit for review": save what's on screen, then hand it to the reviewer.
  const submitReview = async () => {
    if (isCreate) { await save("in_review"); return; }
    const saved = await save(null, { silent: true });
    if (!saved) return;
    try {
      const { data } = await api.post(`/kb/articles/${slug}/review/request`, { reviewer_id: reviewer });
      setArticle(data);
      setStatus(deriveStatus(data));
      toast.success(`Submitted to ${data.reviewer_name || "the reviewer"} — ${data.is_draft ? "hidden until approved" : "readers keep the published version until approved"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const discardPending = async () => {
    if (!window.confirm("Discard the version under review? Readers keep the published version; your unpublished edits are lost.")) return;
    try {
      const { data } = await api.delete(`/kb/articles/${slug}/pending`);
      setArticle(data);
      setStatus(deriveStatus(data));
      setForm((f) => ({
        ...f, name: data.name || "", description: data.description || "", body_html: data.body_html || "",
        meta_description: data.meta_description || "", slug: data.slug || "", keywords: data.keywords || [],
        tags: data.tags || [], sections: data.sections || [], related_ids: data.related_ids || [],
        primary_category_id: data.primary_category_id || "all", glossary_off: data.glossary_off === true,
      }));
      setDirty(false);
      toast.success("Pending version discarded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Swap the dead picture for the chosen one everywhere it appears, then let the
  // ordinary save clear the health alert.
  const replaceFixImage = (newUrl) => {
    const html = swapUrl(form.body_html, fixTarget, newUrl);
    if (html === form.body_html) {
      toast.error("That picture isn't in the article any more");
      return;
    }
    patch({ body_html: html });
    setFixReplaced(true);
    setFixTarget(newUrl);          // keep the panel against the new picture
    toast.success("Swapped in — save to clear the alert");
  };

  const replaceFixEmbed = (newUrl) => {
    const html = swapUrl(form.body_html, embedTarget, newUrl);
    if (html === form.body_html) {
      toast.error("That video isn't in the article any more");
      return;
    }
    patch({ body_html: html });
    setEmbedReplaced(true);
    setEmbedTarget(newUrl);
    toast.success("New video in place — save to clear the alert");
  };

  const goNextEmbed = useCallback(async () => {
    try {
      const { data } = await api.get("/kb/link-health/next",
        { params: { kind: "embed", slug, url: fixEmbedUrl } });
      if (!data.item) {
        toast.success("That was the last broken video — all clear");
        navigate("/kb/image-health");
        return;
      }
      navigate(`/kb/a/${data.item.slug}/edit?fixembed=${encodeURIComponent(data.item.url)}`,
        { replace: true });
      toast.info(`Next: ${data.item.article} — ${data.remaining} left after this one`);
    } catch {
      navigate("/kb/image-health");
    }
  }, [fixEmbedUrl, navigate, slug]);

  // Which picture in the article this is — so a pasted screenshot lands in the
  // media library named after the article and its position.
  const fixNumber = useMemo(() => {
    if (!fixTarget) return 0;
    const srcs = [...(form.body_html || "").matchAll(/<img[^>]+src="([^"]+)"/gi)]
      .map((m) => m[1].replace(/&amp;/g, "&"));
    const at = srcs.indexOf(fixTarget.replace(/&amp;/g, "&"));
    return at >= 0 ? at + 1 : srcs.length + 1;
  }, [fixTarget, form.body_html]);

  // Walking the broken pictures inside this article, without going via the
  // health list — started from the warning banner at the top.
  const startWalk = (srcs) => {
    if (!srcs.length) return;
    setFixQueue(srcs);
    setFixReplaced(false);
    setFixTarget(srcs[0]);
    setFixRemaining(srcs.length - 1);
  };
  const nextInWalk = () => {
    const rest = fixQueue.slice(1);
    setFixQueue(rest);
    setFixReplaced(false);
    setFixTarget(rest[0] || "");
    setFixRemaining(Math.max(0, rest.length - 1));
    if (!rest.length) toast.success("That was the last one in this article");
  };

  const goNextFix = useCallback(async () => {
    try {      const { data } = await api.get("/kb/link-health/next",
        { params: { kind: "image", slug, url: fixUrl } });
      if (!data.item) {
        toast.success("That was the last broken picture — all clear");
        navigate("/kb/image-health");
        return;
      }
      navigate(`/kb/a/${data.item.slug}/edit?fix=${encodeURIComponent(data.item.url)}`,
        { replace: true });
      toast.info(`Next: ${data.item.article} — ${data.remaining} left after this one`);
    } catch {
      navigate("/kb/image-health");
    }
  }, [fixUrl, navigate, slug]);

  // Autosave once a reviewer is set. Edits to a published article land in the
  // pending version, so readers never see a half-finished change.
  useEffect(() => {
    if (isCreate || !dirty || (!reviewer && !isAdmin)) return;
    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => save(null, { silent: true }), 5000);
    return () => clearTimeout(saveRef.current);
  }, [dirty, form, isAdmin, isCreate, reviewer, save]);

  // Live preview should behave exactly like the reader page.
  useEffect(() => {
    if (mode === "preview" && previewRef.current) enhanceKbContent(previewRef.current);
  }, [mode, form.body_html, form.sections]);

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const addKeyword = () => {
    const k = kwDraft.trim().toLowerCase();
    if (!k) return;
    patch({ keywords: [...new Set([...(form.keywords || []), k])].slice(0, 40) });
    setKwDraft("");
  };
  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    patch({ tags: [...new Set([...(form.tags || []), t])].slice(0, 30) });
    setTagDraft("");
  };

  if (loading) return <div className="flex h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>;

  return (
    <div className={`kb-surface ${focus ? "kb-editor-focus" : ""}`} data-testid="kb-editor-page">
      <KbStyleInjector />

      {/* Sticky command bar */}
      <div className="sticky top-0 z-20 -mx-4 mb-4 flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-slate-800 bg-white/95 px-4 py-3 backdrop-blur">
        <Button variant="ghost" size="sm" data-testid="kb-editor-back"
          onClick={() => navigate(isCreate ? "/kb" : `/kb/a/${slug}`)}>
          <ArrowLeft className="mr-1 h-4 w-4" /> {isCreate ? "Cancel" : "Back to article"}
        </Button>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${(PILL[status] || PILL.draft)[1]}`}
          data-testid="kb-editor-status-pill">
          {(PILL[status] || PILL.draft)[0]}
        </span>
        {(isSnippet || article?.is_snippet) && (
          <span className="inline-flex items-center gap-1">
            <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700">Snippet</span>
            <KbSnippetInfo testid="kb-editor-snippet-info" />
          </span>
        )}
        <span className="text-xs text-slate-400 dark:text-slate-500" data-testid="kb-editor-save-state">
          {saving ? "Saving…" : dirty ? "Unsaved changes" : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : ""}
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <div className="flex rounded-md border border-slate-200 dark:border-slate-800 p-0.5">
            {[["visual", "Visual", Settings2], ["html", "HTML", Code2], ["preview", "Preview", Eye]].map(([v, lbl, Icon]) => (
              <button key={v} type="button" data-testid={`kb-editor-mode-${v}`} onClick={() => setMode(v)}
                className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
                  mode === v ? "bg-primary/10 text-primary" : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                <Icon className="h-3.5 w-3.5" /> {lbl}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" data-testid="kb-editor-focus"
            title={focus ? "Show side panel and menus" : "Hide side panel and menus for more room"}
            onClick={() => setFocus((f) => !f)}>
            {focus ? <Minimize2 className="mr-1 h-4 w-4" /> : <Maximize2 className="mr-1 h-4 w-4" />}
            {focus ? "Exit full width" : "Full width"}
          </Button>
          <KbReviewerSelect compact value={reviewer} onChange={(v) => { setReviewer(v); setDirty(true); }}
            testid="kb-editor-reviewer" />
          <Button variant="outline" size="sm" data-testid="kb-editor-save"
            onClick={() => save()} disabled={saving}>
            <Save className="mr-1 h-4 w-4" /> Save
          </Button>
          <Button size="sm" data-testid="kb-editor-submit-review" disabled={saving || !reviewer}
            onClick={submitReview} className="bg-primary text-primary-foreground hover:bg-primary/90"
            title="Publishing goes through the reviewer">
            <Send className="mr-1 h-4 w-4" /> {status === "in_review" ? "Re-submit for review" : "Submit for review"}
          </Button>
          {isAdmin && (status !== "published" || dirty) && (
            <Button variant="outline" size="sm" data-testid="kb-editor-publish-now" disabled={saving}
              onClick={() => {
                const live = status.startsWith("published");
                if (window.confirm(live
                  ? "Publish this version now? As a super admin you skip the reviewer — readers see it immediately."
                  : "Publish now? As a super admin you skip the reviewer — the article goes live immediately.")) save("published");
              }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
              title="Super admin: publish without waiting for the reviewer">
              <ShieldCheck className="mr-1 h-4 w-4" /> {status.startsWith("published") ? "Publish update now" : "Publish now"}
            </Button>
          )}
          {status.startsWith("published") && (
            <Button variant="outline" size="sm" data-testid="kb-editor-unpublish" disabled={saving}
              onClick={() => setBacklinkAsk("unpublish")}>Unpublish</Button>
          )}
        </div>
      </div>

      {backlinkAsk && (
        <KbBacklinkWarning slug={slug} action={backlinkAsk} open
          onOpenChange={(v) => { if (!v) setBacklinkAsk(""); }}
          onConfirm={() => {
            const what = backlinkAsk;
            setBacklinkAsk("");
            if (what === "unpublish") save("draft");
            else archiveArticle();
          }} />
      )}

      <div className={`grid grid-cols-1 gap-6 ${focus ? "" : "xl:grid-cols-[minmax(0,1fr)_340px]"}`}>
        {/* Canvas */}
        <div className="space-y-4">
          {!isCreate && article?.pending && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
              data-testid="kb-editor-pending-banner">
              <ShieldCheck className="h-4 w-4 shrink-0 text-amber-600" />
              <span className="min-w-0 flex-1">
                You&apos;re editing the version under review{article.pending.by_name ? ` (last saved by ${article.pending.by_name})` : ""}.
                Readers still see the version published{article.published_at ? ` on ${new Date(article.published_at).toLocaleDateString()}` : ""} until{" "}
                {article.reviewer_name || "the reviewer"} approves it.
                {!article.pending.submitted && " Not yet submitted — click Submit for review when it's ready."}
              </span>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-800 hover:bg-amber-100" onClick={discardPending}
                data-testid="kb-editor-discard-pending">
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Discard pending version
              </Button>
            </div>
          )}
          {!isCreate && <KbEditingPresence slug={slug} />}
          {!isCreate && !!fixTarget && (
            <KbImageFixBar fixUrl={fixTarget} replaced={fixReplaced} remaining={fixRemaining}
              saving={saving} anchor={fixAnchor} imageNumber={fixNumber}
              pastImages={pastImages} onReplace={replaceFixImage}
              onSaveNext={async () => {
                await save(status);
                if (fixQueue.length) nextInWalk(); else await goNextFix();
              }}
              onSkip={() => { if (fixQueue.length) nextInWalk(); else goNextFix(); }}
              onDone={() => {
                if (fixQueue.length) { setFixQueue([]); setFixTarget(""); setFixRemaining(0); }
                else navigate("/kb/image-health");
              }} />
          )}
          {!isCreate && !!fixEmbedUrl && (
            <KbEmbedFixBar fixUrl={fixEmbedUrl} replaced={embedReplaced} remaining={embedRemaining}
              saving={saving} anchor={embedAnchor} onReplace={replaceFixEmbed}
              onSaveNext={async () => { await save(status); await goNextEmbed(); }}
              onSkip={goNextEmbed} onDone={() => navigate("/kb/image-health")} />
          )}
          {!isCreate && !!form.body_html && (
            <KbBrokenImageWarning bodyHtml={form.body_html}
              onWalk={startWalk}
              onRewrite={(rewrites) => {
                let html = form.body_html;
                Object.entries(rewrites).forEach(([from, to]) => {
                  html = html.split(from).join(to)
                    .split(from.replace(/&/g, "&amp;")).join(to);
                });
                if (html !== form.body_html) patch({ body_html: html });
              }} />
          )}
          {!isCreate && !!form.body_html && (
            <KbBrokenEmbedWarning slug={slug} bodyHtml={form.body_html}
              onRewrite={(rewrites) => {
                let html = form.body_html;
                Object.entries(rewrites).forEach(([from, to]) => {
                  html = html.split(from).join(to)
                    .split(from.replace(/&/g, "&amp;")).join(to);
                });
                if (html !== form.body_html) patch({ body_html: html });
              }} />
          )}
          <Input data-testid="kb-editor-title" value={form.name} placeholder="Article title"
            onChange={(e) => patch({ name: e.target.value })}
            className="h-auto border-0 border-b border-slate-200 dark:border-slate-800 px-0 font-heading text-3xl font-bold shadow-none focus-visible:ring-0" />
          <Input data-testid="kb-editor-description" value={form.description}
            placeholder="Short description shown on cards and search results"
            onChange={(e) => patch({ description: e.target.value })}
            className="border-0 border-b border-dashed border-slate-200 dark:border-slate-800 px-0 text-sm shadow-none focus-visible:ring-0" />

          {mode === "preview" ? (
            <div ref={previewRef} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="kb-editor-preview">
              <div className="mb-4 border-b border-slate-100 dark:border-slate-800 pb-3">
                <h1 className="font-heading text-3xl font-bold text-slate-900 dark:text-slate-100">{form.name || "Untitled"}</h1>
                {form.description && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{form.description}</p>}
              </div>
              <div className="kb-prose" dangerouslySetInnerHTML={{ __html: sanitizeKbHtml(form.body_html || "<p>Nothing written yet.</p>", embedHosts) }} />
              {(form.sections || []).map((s, i) => (
                <div key={s.id || i} className="mt-8 border-t border-dashed border-slate-200 dark:border-slate-800 pt-4">
                  <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    Section — {(s.audiences || []).join(", ") || "no audience"}
                  </div>
                  <div className="kb-prose" dangerouslySetInnerHTML={{ __html: sanitizeKbHtml(s.body_html || "", embedHosts) }} />
                </div>
              ))}
            </div>
          ) : mode === "html" ? (
            <textarea data-testid="kb-editor-html"
              className="min-h-[520px] w-full rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 font-mono text-xs shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              value={form.body_html} onChange={(e) => patch({ body_html: e.target.value })} />
          ) : (
            <KbRichEditor value={form.body_html} onChange={(html) => patch({ body_html: html })} />
          )}

          {!isCreate && (
            <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
              <KbSectionsEditor sections={form.sections} baseHtml={form.body_html}
                onChange={(sections) => patch({ sections })} />
            </div>
          )}
        </div>

        {/* Side panel */}
        <aside className={`space-y-4 xl:sticky xl:top-20 xl:self-start ${focus ? "hidden" : ""}`} data-testid="kb-editor-sidebar">
          <div className="flex rounded-md border border-slate-200 dark:border-slate-800 p-0.5">
            {[["settings", "Settings"], ["review", "Review"], ["history", "History"], ["ai", "AI"]].map(([v, lbl]) => (
              <button key={v} type="button" data-testid={`kb-editor-pane-${v}`} onClick={() => setPane(v)}
                className={`flex-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
                  pane === v ? "bg-primary/10 text-primary" : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                {lbl}
              </button>
            ))}
          </div>

          {pane === "settings" && (
            <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Schedule publish</Label>
                {status.startsWith("published") ? (
                  <p className="text-[11px] text-slate-400 dark:text-slate-500">Already published — unpublish first to schedule it.</p>
                ) : (
                  <>
                    <input type="datetime-local" data-testid="kb-editor-schedule"
                      value={toLocalInput(form.publish_at)}
                      onChange={(e) => patch({ publish_at: fromLocalInput(e.target.value) })}
                      className="h-8 w-full rounded-md border border-slate-200 dark:border-slate-800 px-2 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
                    {form.publish_at ? (
                      <div className="flex items-center justify-between rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
                        data-testid="kb-editor-schedule-pill">
                        <span>Goes live {scheduleLabel(form.publish_at)}</span>
                        <button type="button" data-testid="kb-editor-schedule-clear"
                          onClick={() => patch({ publish_at: "" })}
                          className="font-semibold hover:underline">Clear</button>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-400 dark:text-slate-500">
                        Goes live at this time once the reviewer has approved it. Times are Eastern (Toronto).
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <KbCategoryFilter tree={tree} value={form.primary_category_id} single
                  allLabel="Uncategorised" testid="kb-editor-category"
                  onChange={(id) => patch({ primary_category_id: id })} />
              </div>
              {!isCreate && (
                <div className="space-y-1.5">
                  <Label className="text-xs">URL slug</Label>
                  <Input data-testid="kb-editor-slug" value={form.slug}
                    onChange={(e) => patch({ slug: e.target.value })} className="h-8 font-mono text-xs" />
                  <p className="text-[11px] text-slate-400 dark:text-slate-500">/kb/a/{form.slug || "…"} — changing this breaks old links.</p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Meta description</Label>
                <textarea data-testid="kb-editor-meta" value={form.meta_description} rows={2}
                  maxLength={320} placeholder="Used for search snippets and link previews"
                  onChange={(e) => patch({ meta_description: e.target.value })}
                  className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
                <p className="text-[11px] text-slate-400 dark:text-slate-500">{(form.meta_description || "").length}/320</p>
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-1 text-xs"><Tag className="h-3 w-3" /> Tags <span className="font-normal text-slate-400 dark:text-slate-500">(visible, for filtering)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {(form.tags || []).map((t) => (
                    <span key={t} data-testid={`kb-editor-tag-${t}`}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs text-primary">
                      {t}
                      <button type="button" onClick={() => patch({ tags: form.tags.filter((x) => x !== t) })}
                        className="hover:text-rose-500">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <Input data-testid="kb-editor-tag-input" value={tagDraft} placeholder="Add a tag"
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    className="h-7 text-xs" />
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                    data-testid="kb-editor-tag-add" onClick={addTag}>Add</Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Search keywords <span className="font-normal text-slate-400 dark:text-slate-500">(shown as chips at the foot of the article)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {(form.keywords || []).map((k) => (
                    <span key={k} data-testid={`kb-editor-kw-${k}`}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-xs text-slate-700 dark:text-slate-200">
                      {k}
                      <button type="button" onClick={() => patch({ keywords: form.keywords.filter((x) => x !== k) })}
                        className="text-slate-400 dark:text-slate-500 hover:text-rose-500">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <Input data-testid="kb-editor-kw-input" value={kwDraft} placeholder="Add a keyword"
                    onChange={(e) => setKwDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                    className="h-7 text-xs" />
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                    data-testid="kb-editor-kw-add" onClick={addKeyword}>Add</Button>
                </div>
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                <KbRelatedPicker slug={isCreate ? "" : slug} ids={form.related_ids || []}
                  title={form.name} bodyHtml={form.body_html}
                  categoryId={form.primary_category_id !== "all" ? form.primary_category_id : ""}
                  onChange={(related_ids) => patch({ related_ids })} />
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                <KbVisibilityPicker audiences={form.audiences} teams={form.target_teams}
                  orgs={form.target_orgs} onChange={patch} />
              </div>

              <div className="flex items-start gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
                <input type="checkbox" id="kb-glossary-off" data-testid="kb-editor-glossary-off"
                  checked={!!form.glossary_off} className="mt-0.5"
                  onChange={(e) => patch({ glossary_off: e.target.checked })} />
                <label htmlFor="kb-glossary-off" className="text-xs text-slate-600 dark:text-slate-300">
                  Skip glossary definitions
                  <span className="block text-[11px] text-slate-400 dark:text-slate-500">
                    By default glossary terms are underlined with a hover definition. Tick this to leave this article&apos;s wording untouched.
                  </span>
                </label>
              </div>

              {!isCreate && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                  <Button variant="outline" size="sm" className="w-full text-xs"
                    data-testid="kb-editor-archive" disabled={saving}
                    onClick={() => {
                      if (article?.archived) { archiveArticle(); return; }
                      setBacklinkAsk("archive");
                    }}>
                    <Archive className="mr-1 h-3.5 w-3.5" />
                    {article?.archived ? "Restore from archive" : "Archive this article"}
                  </Button>
                  {article?.archived && (
                    <p className="mt-1.5 text-[11px] text-amber-700" data-testid="kb-editor-archived-note">
                      Archived{article.archived_by ? ` by ${article.archived_by}` : ""} — hidden from readers.
                      A super admin can delete it permanently from Manage articles.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {pane === "review" && (
            <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              {isCreate ? (
                <p className="text-xs text-slate-400 dark:text-slate-500">Save the draft first — then you can request a review.</p>
              ) : (
                <>
                  <KbReviewPanel article={article} slug={slug} reviewer={reviewer} onReviewerChange={setReviewer}
                    onUpdated={(d) => { setArticle(d); setStatus(deriveStatus(d)); setReviewer(d.reviewer_id || ""); }}
                    onActivity={() => setActivity((n) => n + 1)} />
                  <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                    <KbCommentsPanel slug={slug} refreshKey={activity} />
                  </div>
                </>
              )}
            </div>
          )}

          {pane === "ai" && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <KbAiAssistPanel form={form} onApply={patch} />
            </div>
          )}

          {pane === "history" && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              {isCreate ? (
                <p className="text-xs text-slate-400 dark:text-slate-500">History starts once the article exists.</p>
              ) : (
                <KbRevisionsPanel slug={slug} onRestored={(live) => {
                  setArticle(live);
                  setStatus(deriveStatus(live));
                  const d = { ...live, ...((live.pending || {}).fields || {}) };
                  setForm((f) => ({
                    ...f, name: d.name || "", description: d.description || "",
                    body_html: d.body_html || "", sections: d.sections || [],
                    keywords: d.keywords || [], tags: d.tags || [],
                    meta_description: d.meta_description || "",
                    audiences: d.audiences || [], target_teams: d.target_teams || [],
                    target_orgs: d.target_orgs || [],
                  }));
                  if (live.pending) toast.info("Restored into the pending version — submit it for review to go live");
                  setDirty(false);
                }} />
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
